/**
 * [PURPOSE]: Sub-plan 4F-preview — Generic, system-agnostic summary card that
 *   renders an effect's DECLARED shape as a readable one-liner. No character
 *   context needed; it describes the effect object being authored.
 *
 * [WHY]: CW authors need immediate feedback on what their effect will do.
 *   "+2 AC while raging" is more useful than staring at type/op/value fields.
 *   The endgame multi-system data-block engine benefits from a uniform summary
 *   surface that works for ANY system (Rule 11 + Rule 17: no hardcoded names).
 *
 * [API]:
 *   <EffectSummaryCard effect={effectObject} />
 *   // effectObject may be either:
 *   //   EffectBuilderControls shape: { type, operation, value, target, condition }
 *   //   EffectComposer shape:        { kind, attribute, value, condition }
 *
 * [RULE 11 + RULE 17]: Dispatch on effect.type / effect.kind generically.
 *   NO hardcoded per-feat / per-item / per-class names. Reads declared fields only.
 *
 * [EXPORT]: Named export `summarizeEffect(effect)` for unit-testing without DOM.
 */

import React from 'react';

// ---------------------------------------------------------------------------
// Helper — format a value for display
// ---------------------------------------------------------------------------
function fmtValue(value) {
  if (value === null || value === undefined || value === '') return '?';
  if (typeof value === 'object') {
    // Structured value shapes (slotRecoveryAction, pickedAtLevelUp, etc.)
    if (value.pickedAtLevelUp) return '(chosen at level-up)';
    if (value.actionName) return `"${value.actionName}"`;
    return '(complex)';
  }
  const str = String(value);
  // If it starts with a digit or sign, and is short, treat as numeric
  const asNum = Number(str);
  if (!isNaN(asNum) && str.trim() !== '') {
    return asNum >= 0 ? `+${asNum}` : String(asNum);
  }
  return str;
}

// ---------------------------------------------------------------------------
// Helper — format a condition for display
// ---------------------------------------------------------------------------
function fmtCondition(condition) {
  if (!condition) return null;
  if (typeof condition === 'string' && condition && condition !== 'always') return condition;
  if (typeof condition === 'object') {
    if (condition.when && condition.when !== '') return condition.when;
    if (condition.category) {
      const ops = condition.operands && condition.operands.length > 0
        ? condition.operands.join(', ')
        : '';
      return ops ? `${condition.category} ${condition.op || ''} ${ops}`.trim() : condition.category;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Core summarization logic — exported for unit-testing
// ---------------------------------------------------------------------------

/**
 * Produce a human-readable one-liner for an effect object.
 * Works for both EffectBuilderControls shape and EffectComposer (passiveBonus) shape.
 * Returns a string like:
 *   "+2 AC while raging"
 *   "grants resistance: fire"
 *   "advantage on: stealth"
 *   "sets speed to 30"
 *   "grants sense: darkvision / 60 ft"
 *   "flag: reckless_attack"
 */
export function summarizeEffect(effect) {
  if (!effect) return 'No effect';

  const condition = fmtCondition(effect.condition);
  const condSuffix = condition ? ` while ${condition}` : '';

  // ---- EffectComposer shape (kind + attribute/value) ----
  if (effect.kind === 'passiveBonus' && effect.attribute) {
    const val = fmtValue(effect.value);
    const label = effect.attribute; // e.g. 'ac', 'speed', 'attackBonus'
    return `${val} ${label}${condSuffix}`;
  }

  // [SUB-PLAN 3A-followup-effectcomposer-resistance-kind — 2026-05-30]
  // Resistance row: show "grants resistance: <damageType>" or pending label.
  if (effect.kind === 'resistance') {
    const dmgType = effect.value ? String(effect.value) : '(choose damage type)';
    return `grants resistance: ${dmgType}${condSuffix}`;
  }

  // [PB-1 — 2026-06-13]: Vulnerability row — mirror of resistance summary.
  if (effect.kind === 'vulnerability') {
    const dmgType = effect.value ? String(effect.value) : '(choose damage type)';
    return `vulnerability to: ${dmgType}${condSuffix}`;
  }

  // ---- EffectBuilderControls shape (type + operation + value + target) ----
  const type = effect.type || '';
  const op = effect.operation || 'add';
  const val = fmtValue(effect.value);
  const target = effect.target ? `: ${effect.target}` : '';

  switch (type) {
    // Resistance / immunity
    case 'resistance':
      return `grants resistance${target || (val !== '?' ? `: ${effect.value}` : '')}${condSuffix}`;

    // Advantage
    case 'advantage':
      return `advantage on${target}${condSuffix}`;

    // Sense
    case 'sense':
      return `grants sense${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;

    // Proficiency
    case 'proficiency':
      return `proficiency${target}${condSuffix}`;

    // Feature flag
    case 'feature':
      return `flag${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;

    // AC
    case 'ac':
      return op === 'set'
        ? `sets AC to ${effect.value}${condSuffix}`
        : `${val} AC${condSuffix}`;

    // Speed
    case 'speed':
      return op === 'set'
        ? `sets speed to ${effect.value}${condSuffix}`
        : `${val} speed${condSuffix}`;

    // HP (max)
    case 'hp':
      return `${val} max HP${condSuffix}`;

    // Temp HP
    case 'tempHP':
      return `${val} temp HP${condSuffix}`;

    // Attack bonus
    case 'attackBonus':
      return `${val} attack${condSuffix}`;

    // Damage bonus
    case 'damageBonus':
      return `${val} damage${target}${condSuffix}`;

    // Damage reduction
    case 'damageReduction':
      return `reduces damage${target || (val !== '?' ? ` by ${effect.value}` : '')}${condSuffix}`;

    // Skill
    case 'skill':
      return `${val} to skill${target}${condSuffix}`;

    // Initiative
    case 'initiative':
      return `${val} initiative${condSuffix}`;

    // Spell DC
    case 'spellDC':
      return `${val} spell DC${condSuffix}`;

    // Carry capacity
    case 'carryCapacity':
      return op === 'multiply'
        ? `${effect.value}× carry capacity${condSuffix}`
        : `${val} carry capacity${condSuffix}`;

    // Extra attack
    case 'extra_attack':
      return `extra attack${condSuffix}`;

    // Spells
    case 'perRestSpell':
      return `spell (per rest)${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;
    case 'atWillSpell':
      return `at-will spell${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;
    case 'ritualSpell':
      return `ritual spell${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;
    case 'learnedSpell':
      return `learned spell${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;

    // Condition
    case 'causeCondition':
      return `causes condition${val !== '?' ? `: ${effect.value}` : ''}${condSuffix}`;

    // Slot recovery
    case 'slotRecoveryAction': {
      const sra = effect.value && typeof effect.value === 'object' ? effect.value : null;
      return sra && sra.actionName ? `slot recovery: ${sra.actionName}` : 'slot recovery action';
    }

    // Action surfaces
    case 'spendActionMenu': {
      const sam = effect.value && typeof effect.value === 'object' ? effect.value : null;
      return sam && sam.menuId ? `spend menu: ${sam.menuId}` : 'spend action menu';
    }
    case 'reactionTrigger': {
      const rt = effect.value && typeof effect.value === 'object' ? effect.value : null;
      return rt && rt.actionName ? `reaction: ${rt.actionName}` : 'reaction trigger';
    }
    case 'longRestPicker': {
      const lrp = effect.value && typeof effect.value === 'object' ? effect.value : null;
      return lrp && lrp.pickerId ? `long-rest picker: ${lrp.pickerId}` : 'long-rest picker';
    }

    // Triggered effect
    case 'triggeredEffect': {
      const te = effect.value && typeof effect.value === 'object' ? effect.value : null;
      const trigger = te && te.triggerId ? te.triggerId : 'triggered';
      const payloadCount = te && Array.isArray(te.payload) ? te.payload.length : 0;
      return `on ${trigger}: ${payloadCount} effect${payloadCount === 1 ? '' : 's'}`;
    }

    // Critical range
    case 'criticalRange':
      return `crit on ${op === 'min' ? `≤${effect.value}` : effect.value}${condSuffix}`;

    // Hit die recovery
    case 'hitDieRecovery':
      return `${val} HP from hit dice${condSuffix}`;

    // Spell modifier
    case 'spellModifier':
      return `spell modifier${target}${condSuffix}`;

    // Generic fallback — still readable
    default: {
      if (!type) return 'Effect (no type)';
      const dispVal = val !== '?' ? ` ${val}` : '';
      return `${type}${dispVal}${target}${condSuffix}`;
    }
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a compact, readable summary of a single effect object.
 * System-agnostic — works with EffectBuilderControls and EffectComposer shapes.
 */
function EffectSummaryCard({ effect }) {
  if (!effect) return null;
  const summary = summarizeEffect(effect);
  return (
    <p
      data-testid="effect-summary-card"
      className="text-[11px] text-emerald-300/80 italic bg-emerald-950/30 border border-emerald-900/30 rounded px-2 py-0.5 mt-0.5"
    >
      {summary}
    </p>
  );
}

export default EffectSummaryCard;
